import { useState } from 'react';
import { CSVLink } from 'react-csv';
import { createSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  ControlOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  SelectOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Bundle, BundleOwner, EmployeeType, ProductFamily, User } from '@prisma/client';
import { Breadcrumb, Button, Dropdown, Input, Layout, MenuProps, Space, Table } from 'antd';

import { Role } from '@app/domain-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { ConfirmDelete, PageLayout, Tables } from '@app/web-components';

import { ActiveColumn, AuditFilters, CreatedByColumn, EmployeeTypeRenderer, UpdatedByColumn } from '../../common';
import { useDeleteBundle, useFindBundle, useFindProductFamilies } from '../data';
import { useEditBundleAccess } from '../forms/rules';
import { ProductFamilyHierarchyPopover } from '../modals';
import { DetailModal } from '../modals/detail';

export type BundleDetail = Bundle & {
  productFamily: ProductFamily;
  owners: (BundleOwner & { owner: User })[];
  createdByUser: User;
  updatedByUser: User;
};

export const LandingPage = () => {
  const navigate = useNavigate();
  const { user, hasBundleOwnership } = useEditBundleAccess();
  // toggle to show/hide the view dialog
  const [showDetail, setShowDetail] = useState<BundleDetail | undefined>();
  // toggle to show/hide the delete dialog
  const [showBundleDelete, setShowBundleDelete] = useState<BundleDetail | undefined>();

  const del = useDeleteBundle({});
  const deleteValidation = useDeleteBundle({ validateOnly: true });
  const productFamilyQuery = useFindProductFamilies({ where: { bundles: { some: {} } }, orderBy: { name: 'asc' } });

  const actions: MenuProps['items'] = [
    ...(user?.roles[Role.BUNDLE_ADMIN]
      ? [
          {
            label: 'Copy',
            key: 'copy',
            icon: <CopyOutlined />,
          },
          {
            label: 'Edit',
            key: 'edit',
            icon: <EditOutlined />,
          },
          {
            label: 'Manage',
            key: 'manage',
            icon: <ControlOutlined />,
          },
        ]
      : []),
    {
      label: 'Subscribe To',
      key: 'subscribe to',
      icon: <SelectOutlined />,
    },
    ...(user?.roles[Role.BUNDLE_ADMIN]
      ? [
          {
            label: 'Bulk Subscribe To',
            key: 'bulk subscribe to',
            icon: <UploadOutlined />,
          },
          {
            label: 'Delete',
            key: 'delete',
            icon: <DeleteOutlined />,
            danger: true,
          },
        ]
      : []),
  ];

  const handleActionMenuClick = (e: { key: string }, record: BundleDetail) => {
    switch (e.key) {
      case 'copy':
        navigate({
          pathname: './create',
          search: createSearchParams({ id: record.id.toString() }).toString(),
        });
        break;
      case 'subscribe to':
        navigate({
          pathname: '../subscriptions/create',
          search: createSearchParams({ bundleid: record.id.toString() }).toString(),
        });
        break;
      case 'bulk subscribe to':
        navigate({
          pathname: '../subscriptions/bulk',
          search: createSearchParams({ bundleid: record.id.toString() }).toString(),
        });
        break;
      case 'delete':
        setShowBundleDelete(record);
        break;
      default:
        navigate(`./${record.id}/${e.key}`);
        break;
    }
  };

  const handleBundleViewOpen = (bundle: BundleDetail) => {
    setShowDetail(bundle);
  };

  const {
    getCreateButtonProps,
    getCSVLinkProps,
    getParentLayoutContentProps,
    getQuery,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
  } = Tables.useTableState<BundleDetail>(
    'Bundle',
    [
      {
        title: 'Action',
        width: 120,
        render: (_, record) => {
          const filteredActions = actions.filter(
            (item) =>
              item &&
              ((item.key !== 'edit' &&
                item.key !== 'delete' &&
                item.key !== 'manage' &&
                item.key !== 'bulk subscribe to') ||
                hasBundleOwnership(record)),
          );

          return filteredActions.length > 0 ? (
            <Dropdown.Button
              size="small"
              onClick={() => handleBundleViewOpen(record)}
              menu={{
                items: filteredActions,
                onClick: (e) => handleActionMenuClick(e, record),
              }}
            >
              <Space>
                <EyeOutlined />
                Details
              </Space>
            </Dropdown.Button>
          ) : (
            <Button size="small" icon={<EyeOutlined />} onClick={() => handleBundleViewOpen(record)}>
              Details
            </Button>
          );
        },
      },
      {
        title: 'Name',
        dataIndex: 'name',
        width: 350,
        ellipsis: true,
        sorter: true,
      },
      ActiveColumn,
      {
        title: 'Emp Type',
        dataIndex: 'employeeType',
        width: 125,
        responsive: ['md'],
        sorter: true,
        render: (employeeType: EmployeeType) => <EmployeeTypeRenderer employeeType={employeeType} />,
        filters: [
          { text: 'Intel', value: EmployeeType.INTEL },
          { text: 'CW', value: EmployeeType.CONTINGENT_WORKER },
        ],
        filterMultiple: false,
      },
      {
        title: 'Product Family',
        dataIndex: ['productFamily', 'name'],
        width: 350,
        ellipsis: true,
        responsive: ['lg'],
        sorter: true,
        render: (name: string, detail) => {
          return !!user?.roles[Role.BUNDLE_ADMIN] || !!user?.roles[Role.RULE_ADMIN] ? (
            <ProductFamilyHierarchyPopover
              productFamilyId={detail.productFamily?.id}
              productFamilyName={detail.productFamily?.name}
            >
              <Button type="link" size="small" style={{ padding: 0 }}>
                {name}
              </Button>
            </ProductFamilyHierarchyPopover>
          ) : (
            name
          );
        },
        filters: productFamilyQuery.data?.data
          ?.map(({ name }) => ({ text: name, value: name }))
          .sort(({ text: a }, { text: b }) => (a < b ? -1 : a > b ? 1 : 0)), // server side sort isn't working for this
        filterSearch: true,
      },
      { ...UpdatedByColumn, responsive: ['xl'] },
      {
        title: 'Updated On',
        dataIndex: 'updatedOn',
        width: 200,
        ellipsis: true,
        responsive: ['xl'],
        render: (date: string) => formatIsoDate(date),
        sorter: true,
      },
      { ...CreatedByColumn, responsive: ['xxl'] },
      {
        title: 'Created On',
        dataIndex: 'createdOn',
        width: 200,
        ellipsis: true,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
        defaultSortOrder: 'descend', // Based on documentation this should show the column sorted but does not.  Still we've left it here in case that is fixed.
      },
    ],
    {
      customFilters: AuditFilters,
      globalSearchFields: ['name', 'productFamily.name'],
    },
  );

  const queryResult = useFindBundle<BundleDetail>(
    getQuery({
      include: {
        productFamily: true,
        createdByUser: true,
        updatedByUser: true,
        owners: {
          include: { owner: true },
        },
      },
    }),
  );

  return (
    <>
      <ConfirmDelete.Modal
        itemId={showBundleDelete?.id}
        itemName={showBundleDelete?.name}
        onClose={() => setShowBundleDelete(undefined)}
        onOk={del.mutate}
        onValidate={deleteValidation.mutate}
      />
      <DetailModal bundle={showDetail} onClose={() => setShowDetail(undefined)} />
      <Breadcrumb style={{ margin: '16px 0' }} items={[{ title: <Link to="/">Home</Link> }, { title: 'Bundles' }]} />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title="Bundles" />
        <div
          {...getToolbarProps({
            items: [
              <Input {...getSearchInputProps()} placeholder="Search by name or product family..." />,
              user?.roles[Role.BUNDLE_ADMIN] ? <Button {...getCreateButtonProps()} /> : undefined,
              <Button {...getReloadButtonProps()} onClick={() => queryResult.refetch()} />,
              <Button {...getResetFiltersButtonProps()} />,
              <CSVLink {...getCSVLinkProps(queryResult.data?.data)} />,
            ],
          })}
        />

        <Table {...getTableProps(queryResult)} />
      </Layout.Content>
    </>
  );
};
